import { Component, OnDestroy, OnInit } from '@angular/core';
import { FileUploadService } from '../../services/file-upload.service';
import { PredictorModelActions } from 'src/app/state/predictor-model/predictor-model.actions';
import { Select, Store } from '@ngxs/store';
import { AbstractControl, FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { CanvasService } from '../../services/canvas.service';
import { PredictorModelState } from 'src/app/state/predictor-model/predictor-model.state';
import { catchError, Observable, Subject, take, takeUntil } from 'rxjs';
import { CanvasLayer } from 'src/app/global/interfaces/canvas-layer';
import {
  IGeoJSON,
  IGeoJSONFeatureCollection,
  IGeometryGeoJSON,
} from '../../interfaces/geo';
import { NotificationsActions } from 'src/app/state/notifications/notifications.actions';
import { Severity } from 'src/app/global/enums/severity';
import { StudyArea } from '../../interfaces/study-area';
import { TerrainLoadingStatus } from '../../enums/terrain-loading-status';
import { FishnetBatchDecisionModalComponent } from '../fishnet-batch-decision-modal/fishnet-batch-decision-modal.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SimulationService } from '../../services/simulation.service';

@Component({
  selector: 'app-add-files-section',
  templateUrl: './add-files-section.component.html',
  styleUrls: ['./add-files-section.component.scss'],
})
export class AddFilesSectionComponent implements OnInit, OnDestroy {
  @Select(PredictorModelState.demFileUploadStatus)
  demFileUploadStatus$!: Observable<number>;

  @Select(PredictorModelState.terrainLoadingStatus)
  terrainLoadingStatus$!: Observable<TerrainLoadingStatus>;

  @Select(PredictorModelState.selectedSimulationId)
  selectedSimulationId$!: Observable<string>;

  @Select(PredictorModelState.terrainLoaded)
  terrainLoaded$!: Observable<boolean>;

  @Select(PredictorModelState.scenarios)
  scenarios$!: Observable<CanvasLayer[]>;

  @Select(PredictorModelState.shapeFiles)
  shapeFiles$!: Observable<CanvasLayer[]>;

  @Select(PredictorModelState.selectedStudyArea)
  selectedStudyArea$!: Observable<StudyArea | null>;

  shapeFiles: CanvasLayer[] = [];
  validScenarioRanges: string[] = [];
  selectedScenarioRanges: string[] = [];
  numLandslides: number = 0;
  uploadedFishnetShapeFileName: string = '';
  uploadedTerrainFileName: string = '';
  isAddAgentsCompleted: boolean = false;
  TerrainLoadingStatus = TerrainLoadingStatus;

  clearFilesSubject!: Subject<void>;

  // DEM file form
  demFileForm: FormGroup = new FormGroup({});
  demFileModel: FormlyFieldConfig = {};
  demFileFields: FormlyFieldConfig[] = [];

  // ShapeFile form
  shapefileForm: FormGroup = new FormGroup({});
  shapefileFormModel: FormlyFieldConfig = {};
  shapefileFormFields: FormlyFieldConfig[] = [];

  // Scenarios form
  scenariosForm: FormGroup = new FormGroup({});
  scenariosFormModel: {
    startScenario: number;
    endScenario: number;
    specificScenarios: string[];
  } = {
    startScenario: 0,
    endScenario: 0,
    specificScenarios: [],
  };
  scenariosFormFields: FormlyFieldConfig[] = [];

  private readonly FileMaxSizeInMB = 750;
  private ngUnsubscribe = new Subject();

  constructor(
    private fileUploadService: FileUploadService,
    private canvasService: CanvasService,
    private store: Store,
    private dialogService: DialogService,
    private simulationService: SimulationService
  ) {
    this.selectedSimulationId$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.resetForms();
      });

        this.canvasService.isCompleted$.subscribe(value => {
      this.isAddAgentsCompleted = value;
    });      
  }

  ngOnInit() {
    // subscribe to changes to scenarios in state to ensure scenario form field reflects the correct visible ranges
    this.scenarios$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((scenarios: CanvasLayer[]) => {
        // get the visible scenario ranges
        const visibleScenarios: CanvasLayer[] = scenarios.filter(
          (scenario) => scenario.visible
        );
        const visibleScenarioRanges: string[] =
          this.getScenarioRanges(visibleScenarios);

        if (visibleScenarios.length > 0) {
          if (
            JSON.stringify(visibleScenarioRanges) !==
              JSON.stringify(this.selectedScenarioRanges) &&
            !this.isSpecificScenariosFieldActive()
          ) {
            this.selectedScenarioRanges = visibleScenarioRanges;

            // multiple ranges or valid range options are non-contiguous
            if (
              this.selectedScenarioRanges.length > 1 ||
              this.validScenarioRanges.length > 1
            ) {
              this.scenariosFormModel.specificScenarios =
                this.selectedScenarioRanges;
              this.scenariosForm.reset(this.scenariosFormModel);
            }
            // one contiguous range
            else {
              const start: number = Number(visibleScenarios[0].name);
              const end: number = Number(
                visibleScenarios[visibleScenarios.length - 1].name
              );

              this.scenariosFormModel.startScenario = start;
              this.scenariosFormModel.endScenario = end;
              this.scenariosFormModel.specificScenarios = [];
              this.scenariosForm.reset(this.scenariosFormModel);
            }
          }
        } else this.selectedScenarioRanges = [];
      });
    // subscribe to changes to shapeFiles in state to assign to local array
    this.shapeFiles$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((shapeFiles: CanvasLayer[]) => {
        this.shapeFiles = shapeFiles;
      });

    this.selectedStudyArea$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.uploadedTerrainFileName = '';
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  resetForms() {
    this.clearFilesSubject = new Subject<void>();
    this.setFormFields();
    this.demFileForm.reset();
    this.shapefileForm.reset();
    this.scenariosForm.reset();
  }

  setFormFields() {
    this.demFileFields = [
      {
        key: 'fileUploadBox',
        type: 'fileUploadBox',
        props: {
          showTitle: false,
          showSubtitle: false,
          showAcceptedFileTypes: true,
          acceptedFileTypes: 'IMAGE/TIFF, IMAGE/TIF',
          clearFiles: this.clearFilesSubject,
          onFileSelect: (files: File[]) => {
            if (!files || files.length == 0) {
              this.store.dispatch(
                new NotificationsActions.Toast(
                  'No file selected or the selected file has an invalid format.',
                  Severity.Error
                )
              );
              return;
            }
            if (this.fileExceedsMaxSize(files[0])) {
              this.store.dispatch(
                new NotificationsActions.Toast(
                  'The uploaded file exceeds the maximum allowed size (' +
                    this.FileMaxSizeInMB +
                    'MB).',
                  Severity.Error
                )
              );
              return;
            }
            this.uploadDemFile(files[0]);
          },
        },
        id: 'formly_1_fileUploadBox_fileUploadBox_0',
        hooks: {},
        modelOptions: {},
        validation: {
          messages: {},
        },
        wrappers: [],
        expressions: {},
        expressionProperties: {},
      },
    ];

    this.shapefileFormFields = [
      {
        key: 'shapefileUpload',
        type: 'fileUploadBox',
        props: {
          showTitle: false,      
          showSubtitle: false,
          showAcceptedFileTypes: true,
          clearFiles: this.clearFilesSubject,
          multiple:true,
          acceptedFileTypes:
            '.zip, application/zip, application/x-zip-compressed',
          onFileSelect: (files: File[]) => {
            if (!files || files.length == 0) {
              this.store.dispatch(
                new NotificationsActions.Toast(
                  'No file selected or the selected file has an invalid format.',
                  Severity.Error
                )
              );
              return;
            }
            files.forEach(file => {
            if (this.fileExceedsMaxSize(file)) {
              this.store.dispatch(
                new NotificationsActions.Toast(
                  `The uploaded file "${file.name}" exceeds the maximum allowed size (${this.FileMaxSizeInMB}MB).`,
                  Severity.Error
                )
              );
              return; // skip to next file
            }
            this.uploadShapefile(file);
          });
          },
        },
        id: 'formly_1_fileUploadBox_fileUploadBox_1',
        validation: {
          messages: {},
        },
        wrappers: [],
        expressions: {},
        expressionProperties: {},
      },
    ];

    this.scenariosFormFields = [
      {
        fieldGroupClassName: 'p-d-flex',
        className: 'basin-mb-3',
        fieldGroup: [
          {
            key: 'startScenario',
            className:
              'basin-mb-4 basin-mr-4 basin-md-mb-0 basin-width-100 basin-md-width-50 basin-font-size-16 basin-font-weight-400 basin-color-mid-gray',
            type: 'textbox',
            props: {
              label: 'Start Scenario',
              placeholder: '00',
              type: 'number',
              min: 1,
              step: 1,
            },
            validators: {
              startScenario: (control: AbstractControl) => {
                if (!control.value) return true;
                const start: number = Number(control.value);
                const end: number = Number(this.scenariosFormModel.endScenario);
                return start !== null && end !== null ? start <= end : true;
              },
            },
          },
          {
            key: 'endScenario',
            className:
              'basin-width-100 basin-md-width-50 basin-mb-0 basin-font-size-16 basin-font-weight-400 basin-color-mid-gray',
            type: 'textbox',
            props: {
              label: 'End Scenario',
              placeholder: '00',
              type: 'number',
              min: 1,
              step: 1,
            },
            validators: {
              endScenario: (control: AbstractControl) => {
                if (!control.value) return true;
                const start: number = Number(
                  this.scenariosFormModel.startScenario
                );
                const end: number = Number(control.value);
                return start !== null && end !== null ? start <= end : true;
              },
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'p-d-flex',
        className: 'basin-mb-3',
        fieldGroup: [
          {
            key: 'specificScenarios',
            className:
              'basin-mb-4 basin-md-mb-0 basin-width-100 basin-font-size-16 basin-font-weight-400 basin-color-mid-gray',
            type: 'numericRange',
            props: {
              label: 'Specific Scenarios',
              outputFormat: 'array',
              placeholder: 'e.g. 1-5, 8, 11-13',
              validationMessages: {},
            },
          },
        ],
        hooks: {
          onInit: (field: FormlyFieldConfig) => {
            field.formControl?.valueChanges.subscribe(() => {
              this.onScenarioFormFieldChange();
            });
          },
        },
      },
    ];
  }

  public uploadDemFile(file: File) {
    this.uploadedTerrainFileName = file.name;
    this.clearFilesSubject.next();

    const status: TerrainLoadingStatus = this.store.selectSnapshot(
      PredictorModelState.terrainLoadingStatus
    );

    if (
      this.store.selectSnapshot(
        PredictorModelState.selectedStudyAreaHasTerrain
      ) &&
      !TerrainLoadingStatus.isFailure(status)
    ) {
      this.scenarioModeReset();
    }

    this.store.dispatch(new PredictorModelActions.UploadDEMFile(file));
  }

  public removeDemFile() {
    this.uploadedTerrainFileName = '';
  }

  // if in scenario mode, place all agents of visible scenarios of type point
  // otherwise, place all agents of visible shapefiles of type point
  public placeAgents() {
    // Remove previously placed agents before adding new ones
    this.canvasService.clearAllAgents();

    if (this.store.selectSnapshot(PredictorModelState.scenarios).length > 0) {
      this.store
        .selectSnapshot(PredictorModelState.scenarios)
        .filter(
          (scenario: CanvasLayer) =>
            scenario.type === 'Point' && scenario.visible
        )
        .forEach((scenario: CanvasLayer) => {
          this.canvasService.placeAgentsByPointLayer(scenario.layerIndex);
        });
    } else if (this.shapeFiles.length > 0) {
      this.shapeFiles
        .filter((file: CanvasLayer) => file.type === 'Point' && file.visible)
        .forEach((file: CanvasLayer) => {
          this.canvasService.placeAgentsByPointLayer(file.layerIndex);
        });
    }
    // Clear the completed simulation state so the Loop button becomes enabled
    const simulationId: string | undefined = this.store.selectSnapshot(
      PredictorModelState.selectedSimulationId
    );
    if (simulationId) {
      this.simulationService.reset(simulationId);
    }    
    this.canvasService.setAddAgentCompleted(true);
  }

  public uploadShapefile(file: File) {
    this.clearFilesSubject.next();
    this.fileUploadService
      .uploadShapefile(
        file,
        this.store.selectSnapshot(PredictorModelState.selectedSimulationId) ||
          ''
      )
      .pipe(
        catchError((error) => {
          if (error.status === 0) {
            this.store.dispatch(
              new NotificationsActions.Toast(
                'Network error detected. Please check your internet connection.',
                Severity.Error
              )
            );
          } else if (!error.error) {
            this.store.dispatch(
              new NotificationsActions.Toast(
                `An error occurred while uploading the shapefile "${file.name}". Please try again.`,
                Severity.Error
              )
            );
          }

          throw error;
        })
      )
      .subscribe({
        next: (response: IGeoJSONFeatureCollection) => {
          if (
            response === null ||
            !response.features ||
            response.features.length === 0
          ) {
            this.store.dispatch(
              new NotificationsActions.Toast(
                'Unable to upload shapefile.',
                Severity.Error
              )
            );
            return;
          }

          const currentScenarios: CanvasLayer[] = this.store.selectSnapshot(
            PredictorModelState.scenarios
          );
          const uploadedScenarios: IGeoJSONFeatureCollection[] =
            this.splitFeatureCollectionByScenario(response);

          // if previously in scenario mode or multiple scenarios were uploaded, reset the canvas and state
          if (currentScenarios.length > 0 || uploadedScenarios.length > 1) {
            this.scenarioModeReset();
          }

          // if fishnet shapefile was uploaded, add its scenarios to canvas & state & update values & form fields to be shown on UI
          if (uploadedScenarios.length > 1) {
            uploadedScenarios.forEach((scenario) => {
              this.addGeoJSONAsScenario(scenario, scenario.name ?? file.name);
            });

            this.numLandslides = response.features.length;
            this.uploadedFishnetShapeFileName = file.name;
            this.store.dispatch(
              new PredictorModelActions.AddFishnetShapeFileName(file.name)
            );
            this.setScenarioFormFields(uploadedScenarios);

            this.store.dispatch(
              new PredictorModelActions.SetDetailVisibility(false)
            );

            const batchDecisionModal: DynamicDialogRef =
              this.dialogService.open(FishnetBatchDecisionModalComponent, {
                width: '360px',
                modal: true,
                dismissableMask: true,
                styleClass: 'bottom-rounded title-free-header',
                data: {},
              });

            batchDecisionModal.onClose.pipe(take(1)).subscribe();
          }
          // if normal shapefile was uploaded, add it to canvas & state
          else {
            this.store.dispatch(
              new PredictorModelActions.AddFishnetShapeFileName('')
            );
            this.addGeoJSONAsShapeFile(response, file.name);
          }

          // let the user know that the shapefile was uploaded successfully
          this.store.dispatch(
            new NotificationsActions.Toast(
              'Shapefile uploaded successfully.',
              Severity.Success
            )
          );          
          this.canvasService.setAddAgentCompleted(false);
        },
        error: (error) => {
          console.error(error);
          if (error.status === 400 && typeof error.error === 'string') {
            this.store.dispatch(
              new NotificationsActions.Toast(error.error, Severity.Error)
            );
          }
        },
      });
  }

  // function to perform a reset for scenario mode
  // clears all existing geoJSON layers & agents from canvas
  // clears all existing shapeFiles & scenarios from state
  public scenarioModeReset() {
    this.canvasService.clearAllGeoJSON();
    this.canvasService.clearAllAgents();
    this.store.dispatch(new PredictorModelActions.UpdateShapeFiles([]));
    this.store.dispatch(new PredictorModelActions.UpdateScenarios([]));
    this.numLandslides = 0;
    this.uploadedFishnetShapeFileName = '';
  }

  // function to remove a shapefile from canvas & state
  public removeShapeFile(shapeFile: CanvasLayer) {
    this.store.dispatch(new PredictorModelActions.DeleteShapeFile(shapeFile));
    this.canvasService.deleteShapeFile(shapeFile);
  }

  // function to update shapefiles in state & canvas based on local shapefiles array
  public updateShapeFiles() {   
    this.store.dispatch(
      new PredictorModelActions.UpdateShapeFiles([...this.shapeFiles])
    );
   
    if((this.shapeFiles.some(file => file.visible)))
    {
      this.canvasService.setAddAgentCompleted(false);
    }
    else{
      this.canvasService.setAddAgentCompleted(true);
    }
    this.canvasService.setLayerVisibility(this.shapeFiles);
  }

  public reversedShapeFiles(): CanvasLayer[] {
    return [...this.shapeFiles].reverse();
  }

  // function that runs everytime the scenario form field values are updated
  // makes only the scenarios within the range visible
  private onScenarioFormFieldChange() {
    const start: number = Number(this.scenariosFormModel.startScenario);
    const end: number = Number(this.scenariosFormModel.endScenario);
    const specificScenarios: string[] =
      this.scenariosFormModel.specificScenarios;

    let scenarioRangeArray: number[]; // array to hold the range of scenarios selected

    if (this.validScenarioRanges.length > 1) {
      scenarioRangeArray = this.convertRangesToExpandedArray(specificScenarios);
      this.selectedScenarioRanges = specificScenarios;
    } else {
      if (specificScenarios.length > 0) {
        scenarioRangeArray =
          this.convertRangesToExpandedArray(specificScenarios);
        this.selectedScenarioRanges = specificScenarios;
        this.toggleScenarioStartAndEndFields(true);
      } else {
        scenarioRangeArray = this.convertToExpandedArray(start, end);
        this.selectedScenarioRanges =
          start === end ? [`${start}`] : [`${start}-${end}`];
        this.toggleScenarioStartAndEndFields(false);
      }
    }

    // toggle visibility of scenarios based on the range
    const currentScenarios: CanvasLayer[] = this.store.selectSnapshot(
      PredictorModelState.scenarios
    );
    const updatedScenarios: CanvasLayer[] = currentScenarios.map(
      (scenario: CanvasLayer) => {
        if (scenarioRangeArray.includes(Number(scenario.name))) {
          return { ...scenario, visible: true };
        }
        return { ...scenario, visible: false };
      }
    );
    this.canvasService.setLayerVisibility(updatedScenarios);
    this.store.dispatch(
      new PredictorModelActions.UpdateScenarios(updatedScenarios)
    );
  }

  // function to convert start & end values into expanded array of numbers
  private convertToExpandedArray(start: number, end: number): number[] {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  // function to convert array of ranges as strings into expanded array of numbers
  private convertRangesToExpandedArray(ranges: string[]): number[] {
    let output: number[] = [];
    for (const range of ranges) {
      if (range.includes('-')) {
        const start: number = Number(range.split('-')[0]);
        const end: number = Number(range.split('-')[1]);
        output = output.concat(this.convertToExpandedArray(start, end));
      } else {
        output.push(Number(range));
      }
    }
    return output;
  }

  // function to set initial values and max values of scenario form fields
  private setScenarioFormFields(
    uploadedScenarios: IGeoJSONFeatureCollection[]
  ) {
    // get valid scenario ranges from the uploaded scenarios
    this.validScenarioRanges = this.getScenarioRanges(uploadedScenarios);

    const startScenarioField: FormlyFieldConfig | undefined =
      this.scenariosFormFields[0].fieldGroup?.find(
        (field) => field.key === 'startScenario'
      );
    const endScenarioField: FormlyFieldConfig | undefined =
      this.scenariosFormFields[0].fieldGroup?.find(
        (field) => field.key === 'endScenario'
      );
    const specificScenariosField: FormlyFieldConfig | undefined =
      this.scenariosFormFields[1].fieldGroup?.find(
        (field) => field.key === 'specificScenarios'
      );

    if (
      startScenarioField &&
      startScenarioField.props &&
      endScenarioField &&
      endScenarioField.props &&
      specificScenariosField &&
      specificScenariosField.props
    ) {
      specificScenariosField.props['validRanges'] =
        this.validScenarioRanges.join(', ');

      // if the uploaded scenarios are not in a single contiguous range, disable the start & end scenario fields & set the form values
      if (this.validScenarioRanges.length > 1) {
        startScenarioField.props.disabled = true;
        endScenarioField.props.disabled = true;
        const expandedValidRange: number[] = this.convertRangesToExpandedArray(
          this.validScenarioRanges
        );
        this.scenariosFormModel.startScenario = expandedValidRange[0];
        this.scenariosFormModel.endScenario =
          expandedValidRange[expandedValidRange.length - 1];
        this.scenariosFormModel.specificScenarios = this.validScenarioRanges;
      }
      // if the uploaded scenarios are in a single contiguous range, set the start & end scenario form values & max values
      else {
        this.scenariosFormModel.startScenario = Number(
          uploadedScenarios[0].name
        );
        this.scenariosFormModel.endScenario = Number(
          uploadedScenarios[uploadedScenarios.length - 1].name
        );
        startScenarioField.props.max = this.scenariosFormModel.endScenario;
        endScenarioField.props.max = this.scenariosFormModel.endScenario;
      }
    }
    this.scenariosForm.reset(this.scenariosFormModel);
  }

  private toggleScenarioStartAndEndFields(disabled: boolean) {
    const startScenarioField: FormlyFieldConfig | undefined =
      this.scenariosFormFields[0].fieldGroup?.find(
        (field) => field.key === 'startScenario'
      );
    const endScenarioField: FormlyFieldConfig | undefined =
      this.scenariosFormFields[0].fieldGroup?.find(
        (field) => field.key === 'endScenario'
      );

    if (
      startScenarioField &&
      startScenarioField.props &&
      endScenarioField &&
      endScenarioField.props
    ) {
      startScenarioField.props.disabled = disabled;
      endScenarioField.props.disabled = disabled;
    }
  }

  private isSpecificScenariosFieldActive(): boolean {
    const specificScenariosField: HTMLElement | null =
      document.getElementById('numericRange');
    return document.activeElement === specificScenariosField;
  }

  // function to get scenario ranges from provided scenarios, based on their name/number
  // to be used to provide input to new basin component once created
  private getScenarioRanges(
    scenarios: IGeoJSONFeatureCollection[] | CanvasLayer[]
  ): string[] {
    if (scenarios.length === 0) return [];

    const scenarioNumbers: number[] = scenarios.map((scenario) =>
      Number(scenario.name)
    );
    const sortedScenarioNumbers: number[] = Array.from(scenarioNumbers).sort(
      (a, b) => a - b
    );
    const scenarioRanges: string[] = [];
    let start: number = sortedScenarioNumbers[0];
    let end: number = sortedScenarioNumbers[0];
    for (let i: number = 1; i < sortedScenarioNumbers.length; i++) {
      if (sortedScenarioNumbers[i] === end + 1) {
        end = sortedScenarioNumbers[i];
      } else {
        if (start === end) {
          scenarioRanges.push(`${start}`);
        } else {
          scenarioRanges.push(`${start}-${end}`);
        }
        start = sortedScenarioNumbers[i];
        end = sortedScenarioNumbers[i];
      }
    }
    if (start === end) {
      scenarioRanges.push(`${start}`);
    } else {
      scenarioRanges.push(`${start}-${end}`);
    }
    return scenarioRanges;
  }

  // splits a single feature collection into multiple feature collections based on the scenario number
  private splitFeatureCollectionByScenario(
    geojson: IGeoJSONFeatureCollection
  ): IGeoJSONFeatureCollection[] {
    const groupedFeatures: Map<number, IGeoJSON<IGeometryGeoJSON>[]> =
      new Map();

    // group features based on scenario number (-1 if no scenario)
    geojson.features.forEach((feature) => {
      const key: number =
        (feature.properties as { Scenario?: number | string })?.Scenario !==
        undefined
          ? Number(
              (feature.properties as { Scenario?: number | string }).Scenario
            )
          : -1;
      if (!groupedFeatures.has(key)) {
        groupedFeatures.set(key, []);
      }
      groupedFeatures.get(key)?.push(feature);
    });
    // sort grouped features by scenario number
    const sortedFeatures: [number, IGeoJSON<IGeometryGeoJSON>[]][] = Array.from(
      groupedFeatures.entries()
    ).sort(([keyA], [keyB]) => {
      return keyA - keyB;
    });

    // return an array of FeatureCollections
    return sortedFeatures.map(([key, features]) => ({
      name: `${key}`,
      type: 'FeatureCollection',
      features: features,
    }));
  }

  // adds geojson as a shapefile to canvas & to state
  private addGeoJSONAsShapeFile(
    geojson: IGeoJSONFeatureCollection,
    name: string
  ) {
    const layerIndex: number = this.canvasService.addGeoJSON(geojson);
    const shapeFile: CanvasLayer = {
      name: name,
      simulationId:
        this.store.selectSnapshot(PredictorModelState.selectedSimulationId) ||
        '',
      type: geojson.features[0].geometry.type,
      layerIndex: layerIndex,
      visible: true,
    };
    this.store.dispatch(new PredictorModelActions.AddShapeFile(shapeFile));
  }

  // adds geojson as a scenario to canvas & to state
  private addGeoJSONAsScenario(
    geojson: IGeoJSONFeatureCollection,
    name: string
  ) {
    const layerIndex: number = this.canvasService.addGeoJSON(geojson, true);
    const scenario: CanvasLayer = {
      name: name,
      type: geojson.features[0].geometry.type,
      simulationId:
        this.store.selectSnapshot(PredictorModelState.selectedSimulationId) ||
        '',
      layerIndex: layerIndex,
      visible: true,
    };
    this.store.dispatch(new PredictorModelActions.AddScenario(scenario));
  }

  private fileExceedsMaxSize(file: File): boolean {
    const fileMaxSizeInBytes: number = this.FileMaxSizeInMB * 1_000_000;
    return file.size > fileMaxSizeInBytes;
  }
}
